import { Component } from "@theme/component";

// Subscribe-mode intro (first-order) prices by box count, in cents — the
// hardcoded "first box" prices that mirror the automatic discounts. DISPLAY
// ONLY: the variant's live subscribePrice is the renewal price; the variant
// price posted to cart is unchanged. Keep in sync with the admin discounts.
const INTRO_SUBSCRIBE_PRICES = { 2: 5500, 3: 7500, 4: 8400 };

/**
 * Flexible bundle builder.
 *
 * The shopper builds a bundle of N boxes (min..max). Each box is one of the
 * configured flavors. The bundle is sold as a single product (the "bundle
 * handle") that has one variant per box count (2,3,4,5,6 ...). More boxes =
 * a cheaper per-box price, which lives entirely in those variant prices.
 *
 * On every add/remove this component:
 *   - swaps the form's hidden variant `id` to the variant matching the count,
 *   - repaints the inline price block (one-time vs subscribe) from live JSON,
 *   - writes the chosen flavors as line-item properties + a `_bundle` blob.
 *
 * Subscriptions: a single Subscribe & Save selling-plan group on the bundle
 * product applies to every size variant, so the plan id is constant across
 * counts and the per-size subscribe price comes from each variant's
 * selling_plan_allocations. We read both from `/products/{handle}.js`.
 *
 * @extends {Component}
 */
class BundleBuilderFlexComponent extends Component {
  requiredRefs = [];

  /** Flavor products keyed by handle (for the flavor grid + box thumbnails). */
  #products = new Map();

  /** Size variants: [{ boxes, id, price, compareAt, subscribePrice }] sorted asc. */
  #variants = [];

  /** Subscribe & Save selling plan id (constant across size variants), or null. */
  #sellingPlanId = null;

  /** Active boxes: [{ productId, title, image, product_id, variant_id, sku }]. */
  #boxes = [];

  #min = 2;
  #max = 6;
  #subscriptionEnabled = true;
  #displayDiscountPercent = 0;

  /**
   * Per-contract id sent as `properties[_subscription_uuid]` on subscription
   * adds (backend contract: lets renewals map to the saved bundle even when
   * two subscriptions share a selling plan). Regenerated whenever the box
   * composition changes, so re-adding the SAME build merges into one cart
   * line/contract while a different build becomes its own contract.
   */
  #subscriptionUuid = null;

  // ---- URL / image helpers (shared shape with bundle-builder.js) ----

  #toAbsoluteUrl(url) {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("//")) return window.location.protocol + url;
    if (url.startsWith("/")) return window.location.origin + url;
    return window.location.origin + "/" + url;
  }

  #findImageByAltTag(product, altTag) {
    if (!product || !altTag) return "";
    if (Array.isArray(product.media)) {
      const m = product.media.find((media) => media.media_type === "image" && (media.alt || "").toLowerCase().includes(altTag.toLowerCase()));
      if (m) return this.#toAbsoluteUrl(m.preview_image?.src || m.src || m.url || "");
    }
    if (Array.isArray(product.images)) {
      const img = product.images.find((i) => i && typeof i === "object" && (i.alt || "").toLowerCase().includes(altTag.toLowerCase()));
      if (img) return this.#toAbsoluteUrl(img.src || img.url || "");
    }
    return "";
  }

  #imageForGrid(product) {
    let url = this.#findImageByAltTag(product, "bundle-selector");
    if (!url && product.featured_image) url = typeof product.featured_image === "string" ? product.featured_image : product.featured_image.src || "";
    if (!url && product.images?.length) {
      const f = product.images[0];
      url = typeof f === "string" ? f : f?.src || f?.url || "";
    }
    return this.#toAbsoluteUrl(url);
  }

  #imageForBox(product) {
    let url = this.#findImageByAltTag(product, "bundle-box");
    if (!url && product.images?.length) {
      const f = product.images[0];
      url = typeof f === "string" ? f : f?.src || f?.url || "";
    }
    if (!url && product.featured_image) url = typeof product.featured_image === "string" ? product.featured_image : product.featured_image.src || "";
    return this.#toAbsoluteUrl(url);
  }

  // ---- lifecycle ----

  connectedCallback() {
    super.connectedCallback();

    this.#min = parseInt(this.getAttribute("data-min-boxes")) || 1;
    this.#max = parseInt(this.getAttribute("data-max-boxes")) || 8;
    this.#subscriptionEnabled = this.getAttribute("data-subscription-enabled") !== "false";
    const displayDiscountPercent = parseInt(this.getAttribute("data-display-discount-percent"));
    this.#displayDiscountPercent = Number.isFinite(displayDiscountPercent)
      ? Math.min(Math.max(displayDiscountPercent, 0), 100)
      : 0;
    this.#subscriptionUuid = this.#newUuid();

    this.#setupEventListeners();
    this.#wireDragScroll();

    // Final sync before the cart-add wrapper serializes the form: capture
    // phase on this ancestor runs before the wrapper's submit handler, so the
    // uuid property reflects the selling-plan input's final state.
    this.addEventListener("submit", () => this.#syncSubscriptionUuid(), true);

    Promise.all([this.#loadFlavorProducts(), this.#loadBundleVariants()]).then(() => {
      this.#renderFlavorGrid();
      // Start COMPLETE by default. Box sources in priority order: a `?boxes=`
      // URL param (shareable custom-bundle links — a fresh link must show
      // exactly the build the sender intended, so it beats the saved
      // session), then the shopper's saved build from this session (the PDP
      // Single/Bundle toggle is a real navigation), then the configured
      // starting size. The default path is one decision — buy it — but the shopper
      // can clear boxes and reconfigure freely from there.
      if (!this.#applyBoxesFromUrl()) {
        this.#restoreBoxes();
        if (this.#boxes.length === 0) this.#fillBoxes(this.#initialCount());
      }
      const grid = this.querySelector('[ref="boxesGrid"]');
      if (grid) grid.dataset.boxCount = String(this.#boxes.length);
      this.#render();
    });
  }

  /** The pre-built starting size when it exists, else the smallest purchasable. */
  #initialCount() {
    const initial = this.#indicativeVariant();
    if (initial) return Math.min(Math.max(initial.boxes, this.#min), this.#max);
    return this.#min;
  }

  /** Fill with default-rotation flavors up to `target` (never removes). */
  #fillBoxes(target) {
    while (this.#boxes.length < target && this.#boxes.length < this.#max) {
      const box = this.#boxFromFlavor(this.#defaultFlavorId(this.#boxes.length));
      if (!box) break;
      this.#boxes.push(box);
    }
  }

  /**
   * Prefill from a `?boxes=` URL param, one token per box:
   * `?boxes=orange,lemonade` (`/` and `|` also work as
   * separators). Tokens fuzzy-match the configured flavors — exact handle
   * first, then handle substring, then product-title substring — so links
   * stay human-writable ("berry" finds the berry flavor without the exact
   * handle). An unavailable flavor resolves to its configured replacement;
   * unknown tokens are skipped. The list caps at the max box count.
   * Returns true when at least one box was built; the build then persists to
   * the session like any hand-made one (via the #render → #saveBoxes chain).
   */
  #applyBoxesFromUrl() {
    let tokens;
    try {
      const raw = new URLSearchParams(window.location.search).get("boxes");
      if (!raw) return false;
      tokens = raw
        .split(/[,/|]+/)
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    } catch (e) {
      return false;
    }
    const handles = this.#configuredFlavorHandles();
    for (const token of tokens) {
      if (this.#boxes.length >= this.#max) break;
      const handle =
        handles.find((h) => h.toLowerCase() === token) ||
        handles.find((h) => h.toLowerCase().includes(token)) ||
        handles.find((h) => (this.#products.get(h)?.title || "").toLowerCase().includes(token));
      const box = handle ? this.#boxFromFlavor(this.#replaceUnavailableFlavor(handle)) : null;
      if (box) this.#boxes.push(box);
    }
    return this.#boxes.length > 0;
  }

  // ---- session persistence (survives Single/Bundle toggle navigations) ----

  #storageKey() {
    return `ss:flexBundleBoxes:${this.getAttribute("data-bundle-handle") || "bundle"}`;
  }

  #saveBoxes() {
    try {
      sessionStorage.setItem(this.#storageKey(), JSON.stringify(this.#boxes.map((b) => b.productId)));
    } catch (e) {}
  }

  #restoreBoxes() {
    try {
      const raw = sessionStorage.getItem(this.#storageKey());
      if (!raw) return;
      const handles = JSON.parse(raw);
      if (!Array.isArray(handles)) return;
      for (const handle of handles.slice(0, this.#max)) {
        const box = this.#boxFromFlavor(this.#replaceUnavailableFlavor(handle));
        if (box) this.#boxes.push(box);
      }
    } catch (e) {}
  }

  async #loadFlavorProducts() {
    const handles = [
      ...new Set(
        [
          this.getAttribute("data-product-id-1"),
          this.getAttribute("data-product-id-2"),
          this.getAttribute("data-product-id-3"),
          this.getAttribute("data-product-id-4"),
          ...this.#unavailableFlavorHandles(),
          this.getAttribute("data-unavailable-replacement-product-id"),
        ].filter(Boolean)
      ),
    ];

    await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (!res.ok) return;
          const product = await res.json();
          this.#products.set(handle, product);
        } catch (e) {
          console.error("[bundle-flex] flavor load failed", handle, e);
        }
      })
    );
  }

  async #loadBundleVariants() {
    const handle = this.getAttribute("data-bundle-handle");
    if (!handle) return;
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) {
        console.error("[bundle-flex] bundle product not found:", handle, res.status);
        return;
      }
      const product = await res.json();

      // Subscribe & Save plan id (first plan of the first group, if any).
      const group = product.selling_plan_groups?.[0];
      this.#sellingPlanId = group?.selling_plans?.[0]?.id || null;

      // One variant per box count. The size lives in the variant's single
      // option value ("2".."6") — fall back to parsing the title.
      this.#variants = product.variants
        .map((v) => {
          const boxes = parseInt((v.options && v.options[0]) ?? v.title);
          let subscribePrice = null;
          const alloc = (v.selling_plan_allocations || []).find((a) => !this.#sellingPlanId || a.selling_plan_id === this.#sellingPlanId);
          if (alloc) subscribePrice = alloc.price;
          return { boxes, id: v.id, price: v.price, compareAt: v.compare_at_price || null, subscribePrice };
        })
        .filter((v) => Number.isFinite(v.boxes))
        .sort((a, b) => a.boxes - b.boxes);

      if (this.#variants.length) {
        this.#min = Math.max(this.#min, this.#variants[0].boxes);
        this.#max = Math.min(this.#max, this.#variants[this.#variants.length - 1].boxes);
      }
    } catch (e) {
      console.error("[bundle-flex] bundle variant load failed", e);
    }
  }

  // ---- flavor grid (unchanged shape from bundle-builder.js) ----

  #renderFlavorGrid() {
    const grid = this.querySelector('[ref="flavorGrid"]');
    if (!grid) return;
    grid.querySelectorAll('[ref="flavorItem"]').forEach((item) => {
      const product = this.#products.get(item.dataset.productId);
      const name = item.querySelector('[ref="flavorName"]');
      const image = item.querySelector('[ref="flavorImage"]');
      const unavailable = this.#isFlavorUnavailable(item.dataset.productId);
      item.classList.toggle("bundle-flavor-grid__item--unavailable", unavailable);
      item.toggleAttribute("data-unavailable", unavailable);
      if (unavailable) item.setAttribute("aria-disabled", "true");
      else item.removeAttribute("aria-disabled");
      let stockStatus = item.querySelector(".bundle-flavor-grid__stock-status");
      if (unavailable && !stockStatus) {
        stockStatus = document.createElement("span");
        stockStatus.className = "bundle-flavor-grid__stock-status";
        stockStatus.textContent = "Out of stock";
        item.prepend(stockStatus);
      } else if (!unavailable && stockStatus) {
        stockStatus.remove();
      }
      if (!product) {
        if (name && name.textContent === "Loading...") name.textContent = "";
        return;
      }
      if (name) name.textContent = product.title || "";
      if (image) {
        const url = this.#imageForGrid(product);
        if (url) {
          image.src = url;
          image.alt = product.title || "";
        }
      }
      item.dataset.productTitle = product.title || "";
    });
  }

  #setupEventListeners() {
    this.addEventListener("click", (event) => {
      const flavorItem = event.target.closest('[ref="flavorItem"]');
      if (flavorItem) {
        this.#addBox(flavorItem.dataset.productId);
        return;
      }
      const removeBtn = event.target.closest('[ref="removeButton"]');
      if (removeBtn) {
        const slot = removeBtn.closest(".bundle-box-selector");
        if (slot) this.#removeBox(parseInt(slot.dataset.boxNumber) - 1);
        return;
      }
      // Empty "+ Add Box" invitation slots add the next default flavor.
      const inviteSlot = event.target.closest(".bundle-box-selector--invite");
      if (inviteSlot) {
        this.#addBox(this.#defaultFlavorId(this.#boxes.length));
      }
    });

    // Repaint price + the priced ATC label when the shopper toggles
    // Subscribe vs One-Time. Any change inside the form (radios, plan
    // dropdown) also re-syncs the subscription uuid property.
    this.addEventListener("change", (event) => {
      const t = event.target;
      if (t.classList?.contains("subscription-form__radio-subscribe") || t.classList?.contains("subscription-form__radio-onetime")) {
        this.#updatePrice();
        this.#updateAddToCartButton();
      }
      this.#syncSubscriptionUuid();
    });
  }

  /**
   * Swiper-style mouse drag on the boxes row. Touch devices scroll natively;
   * this gives desktop the same click-and-drag feel. A small movement
   * threshold separates drags from clicks, and a capture-phase click handler
   * swallows the click that ends a drag so it can't add/remove a box.
   */
  #wireDragScroll() {
    const grid = this.querySelector('[ref="boxesGrid"]');
    if (!grid) return;

    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let dragged = false;

    grid.addEventListener("dragstart", (e) => e.preventDefault());

    grid.addEventListener("pointerdown", (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = grid.scrollLeft;
      dragged = false;
    });

    grid.addEventListener("pointermove", (e) => {
      if (e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      if (!dragged && Math.abs(dx) > 5) {
        dragged = true;
        grid.classList.add("is-dragging");
        grid.setPointerCapture(pointerId);
      }
      if (dragged) grid.scrollLeft = startScroll - dx;
    });

    const release = (e) => {
      if (e.pointerId !== pointerId) return;
      pointerId = null;
      grid.classList.remove("is-dragging");
    };
    grid.addEventListener("pointerup", release);
    grid.addEventListener("pointercancel", release);

    // Swallow the click that ends a drag (capture runs before the component's
    // own click handling).
    grid.addEventListener(
      "click",
      (e) => {
        if (!dragged) return;
        dragged = false;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
  }

  /** Flavor handles in configured order, including products that are sold out. */
  #configuredFlavorHandles() {
    return [
      this.getAttribute("data-product-id-1"),
      this.getAttribute("data-product-id-2"),
      this.getAttribute("data-product-id-3"),
      this.getAttribute("data-product-id-4"),
    ].filter((h) => h && this.#products.has(h));
  }

  /** Manually paused flavor handles configured as a comma-separated list. */
  #unavailableFlavorHandles() {
    return (this.getAttribute("data-unavailable-product-ids") || "")
      .split(",")
      .map((handle) => handle.trim())
      .filter(Boolean);
  }

  #isFlavorUnavailable(productId) {
    if (!productId) return true;
    if (this.#unavailableFlavorHandles().includes(productId)) return true;
    const product = this.#products.get(productId);
    return !product || !(product.variants || []).some((variant) => variant.available);
  }

  #replaceUnavailableFlavor(productId) {
    if (!this.#isFlavorUnavailable(productId)) return productId;
    const configuredReplacement = this.getAttribute("data-unavailable-replacement-product-id");
    if (configuredReplacement && !this.#isFlavorUnavailable(configuredReplacement)) return configuredReplacement;
    return this.#configuredFlavorHandles().find((handle) => !this.#isFlavorUnavailable(handle)) || null;
  }

  #defaultFlavorId(index) {
    const handles = [
      ...new Set(
        this.#configuredFlavorHandles()
          .map((handle) => this.#replaceUnavailableFlavor(handle))
          .filter(Boolean)
      ),
    ];
    if (!handles.length) return null;
    return handles[index % handles.length];
  }

  #boxFromFlavor(productId) {
    if (this.#isFlavorUnavailable(productId)) return null;
    const product = this.#products.get(productId);
    if (!product) return null;
    // Prefer a sellable variant. The backend order-edits the slot's
    // variant_id onto the order and SILENTLY DROPS slots whose variant_id is
    // missing — so a flavor without a usable variant must not become a box.
    const variant = (product.variants || []).find((v) => v.available);
    if (!variant || !Number.isFinite(Number(variant.id))) return null;
    return {
      productId,
      title: product.title || "Unknown Product",
      image: this.#imageForBox(product),
      product_id: product.id || null,
      variant_id: variant.id,
      sku: variant.sku || null,
    };
  }

  #addBox(productId) {
    if (this.#boxes.length >= this.#max) return;
    const box = this.#boxFromFlavor(productId);
    if (!box) return;
    this.#boxes.push(box);
    this.#subscriptionUuid = this.#newUuid();
    this.#render();
  }

  #removeBox(index) {
    if (index < 0 || index >= this.#boxes.length) return;
    this.#boxes.splice(index, 1);
    this.#subscriptionUuid = this.#newUuid();
    this.#render();
  }

  // ---- subscription contract id ----

  #newUuid() {
    if (window.crypto?.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /**
   * Fill + enable `properties[_subscription_uuid]` only while a selling plan
   * is selected (the shared form clears its hidden `selling_plan` input for
   * one-time purchases). Disabled inputs never reach checkout.
   */
  #syncSubscriptionUuid() {
    const form = this.querySelector("form") || this.querySelector(".bundle-product-form");
    if (!form) return;
    const uuidInput = form.querySelector('[ref="subscriptionUuid"]');
    if (!uuidInput) return;
    const planInput = form.querySelector('[name="selling_plan"]');
    const subscribed = !!(planInput && planInput.value);
    uuidInput.disabled = !subscribed;
    uuidInput.value = subscribed ? this.#subscriptionUuid : "";
  }

  // ---- rendering ----

  #render() {
    this.#renderBoxes();
    this.#updatePrice();
    this.#updateFormProperties();
    this.#updateAddToCartButton();
    this.#saveBoxes();
  }

  #renderBoxes() {
    const grid = this.querySelector('[ref="boxesGrid"]');
    if (!grid) return;
    const count = this.#boxes.length;
    const prevCount = parseInt(grid.dataset.boxCount) || 0;
    grid.dataset.boxCount = String(count);

    // Always show at least 4 slots: filled boxes first, then "Select a Box to
    // Add" placeholders (the original builder's resting look). Every filled
    // box keeps its X — the shopper can clear the whole bundle and rebuild it
    // however they want; the ATC simply disables below the minimum.
    const visibleSlots = Math.max(Math.min(4, this.#max), count);
    const slots = grid.querySelectorAll(".bundle-box-selector");
    slots.forEach((slot, idx) => {
      const active = idx < count;
      slot.hidden = idx >= visibleSlots;
      const placeholder = slot.querySelector('[ref="placeholder"]');
      const selected = slot.querySelector('[ref="selected"]');
      const img = slot.querySelector('[ref="productImageSrc"]');
      if (active) {
        const box = this.#boxes[idx];
        slot.classList.remove("bundle-box-selector--invite");
        if (placeholder) placeholder.classList.add("hidden");
        if (selected) selected.classList.remove("hidden");
        if (img) {
          img.src = box.image || "";
          img.alt = box.title || "";
        }
      } else {
        // Empty slots render as dashed "+ Add Box" invitations and are
        // clickable (they add the next default flavor).
        slot.classList.add("bundle-box-selector--invite");
        const content = slot.querySelector(".bundle-box-selector__placeholder-content");
        if (content && !content.dataset.invite) {
          content.dataset.invite = "true";
          content.innerHTML =
            '<span class="bundle-builder-block__add-box-plus" aria-hidden="true">+</span>' +
            '<span class="bundle-builder-block__add-box-text">Add Box</span>';
        }
        if (placeholder) placeholder.classList.remove("hidden");
        if (selected) selected.classList.add("hidden");
        if (img) {
          img.src = "";
          img.alt = "";
        }
      }
    });

    // The row scrolls horizontally past four boxes; on growth, slide to the
    // end so the newest boxes are in view (half a box peeks on the left,
    // signalling the row scrolls).
    if (count > prevCount && grid.scrollWidth > grid.clientWidth) {
      grid.scrollTo({ left: grid.scrollWidth - grid.clientWidth, behavior: "smooth" });
    }
  }

  #currentVariant() {
    const count = this.#boxes.length;
    return this.#variants.find((v) => v.boxes === count) || null;
  }

  /**
   * The configured first-load variant. Falls back to the smallest purchasable
   * size when that variant is absent.
   */
  #indicativeVariant() {
    const configured = parseInt(this.getAttribute("data-initial-boxes"));
    const initialBoxes = Number.isFinite(configured) ? Math.min(Math.max(configured, this.#min), this.#max) : 4;
    return (
      this.#variants.find((v) => v.boxes === initialBoxes) ||
      this.#variants.find((v) => v.boxes >= this.#min) ||
      this.#variants[0] ||
      null
    );
  }

  #variantForBoxes(boxes) {
    return this.#variants.find((v) => v.boxes === boxes) || null;
  }

  #isSubscribeSelected() {
    const sub = this.querySelector(".subscription-form__radio-subscribe");
    const one = this.querySelector(".subscription-form__radio-onetime");
    if (sub && sub.checked) return true;
    if (one && one.checked) return false;
    // Blocks without subscription controls are one-time purchases.
    return this.#subscriptionEnabled;
  }

  #basePrice(variant, boxes, subscribe) {
    if (!variant) return null;
    const subPrice = variant.subscribePrice != null ? variant.subscribePrice : variant.price;
    return subscribe ? (this.#introPrice(boxes) ?? subPrice) : variant.price;
  }

  #displayPrice(variant, boxes, subscribe) {
    const basePrice = this.#basePrice(variant, boxes, subscribe);
    if (basePrice == null || this.#displayDiscountPercent <= 0) return basePrice;
    return Math.round(basePrice * (100 - this.#displayDiscountPercent) / 100);
  }

  /**
   * Keep the Single/Bundle toggle's BUNDLE side tracking the CURRENT bundle
   * size: per-stick price + stick count recalculate on every box add/remove
   * (e.g. 4 boxes → "$1.09 / stick | 112ct", 8 boxes → "$0.91 / stick |
   * 224ct"). Both mode texts are written to the data attrs so the shared
   * form's Subscribe/One-Time swap flips between rates of the SAME size.
   * Floor (not round) per stick, matching the shared form's convention.
   * Below the minimum, the minimum size is shown as the indicative rate.
   */
  #updateToggleTexts(variant, boxes) {
    if (!variant || !boxes) return;
    const scope =
      this.closest(".product-details") ||
      this.closest('[id^="ProductDetails"]') ||
      this.closest("product-information") ||
      this.closest(".shopify-section") ||
      document;

    const STICKS_PER_BOX = 28;
    const sticks = boxes * STICKS_PER_BOX;
    const fmt = (cents) => `$${(Math.floor(cents / sticks) / 100).toFixed(2)} / stick`;

    const onetimeText = fmt(this.#displayPrice(variant, boxes, false));
    const subscribeText = fmt(this.#displayPrice(variant, boxes, true));

    scope.querySelectorAll('[data-side="bundle"] .single-bundle-toggle__option-price').forEach((el) => {
      el.setAttribute("data-onetime-text", onetimeText);
      el.setAttribute("data-subscription-text", subscribeText);
      el.textContent = this.#isSubscribeSelected() ? subscribeText : onetimeText;
    });
    scope.querySelectorAll('[data-side="bundle"] .single-bundle-toggle__option-sublabel').forEach((el) => {
      el.textContent = `${sticks}ct`;
    });
  }

  /** Whole-dollar display, rounded to nearest ($112.14 → $112). Display only — checkout charges the exact variant price. */
  #formatMoney(cents) {
    if (cents == null) return "";
    return "$" + Math.round(cents / 100).toLocaleString("en-US");
  }

  /** Hardcoded subscribe intro (first-order) price for a box count (cents), or null. */
  #introPrice(boxes) {
    return INTRO_SUBSCRIBE_PRICES[boxes] ?? null;
  }


  #updatePrice() {
    const count = this.#boxes.length;
    const priceGroup = this.querySelector('[ref="priceGroup"]');
    if (priceGroup) priceGroup.hidden = false;

    // The bundle is always a complete build, so this is normally an exact
    // size match. The fallbacks only matter before the first fill or if a
    // saved state predates the current variant range.
    // Below the minimum the price line shows only the prompt ("Add 2 or
    // more boxes") — no dollar values until the bundle is purchasable.
    const qualifier = this.querySelector('[ref="priceQualifier"]');
    if (count < this.#min) {
      if (qualifier) {
        qualifier.textContent = `Add ${this.#min} or more boxes`;
        qualifier.hidden = false;
      }
      if (priceGroup) priceGroup.hidden = true;
      const cg = this.querySelector('[ref="compareGroup"]');
      if (cg) cg.hidden = true;
      const sv = this.querySelector('[ref="savings"]');
      if (sv) sv.hidden = true;
      const indicative = this.#variants.find((v) => v.boxes >= this.#min) || this.#indicativeVariant();
      if (indicative) this.#updateToggleTexts(indicative, indicative.boxes);
      return;
    }
    if (qualifier) qualifier.hidden = true;

    const exact = this.#currentVariant();
    const variant = exact || this.#variants.find((v) => v.boxes >= count) || this.#indicativeVariant();

    // Only point the form at a real, purchasable variant.
    const variantInput = this.querySelector('[ref="variantId"]');
    if (variantInput && exact) variantInput.value = exact.id;

    const priceEl = this.querySelector('[ref="price"]');
    if (!variant || !priceEl) return;

    const subscribe = this.#isSubscribeSelected();
    // Box count being priced (exact build, else the fallback variant's size).
    const pricedBoxes = exact ? count : variant.boxes;
    const basePrice = this.#basePrice(variant, pricedBoxes, subscribe);
    // Subscribe shows the hardcoded intro (first-order) price when set; one-time
    // shows full price. A block-level landing-page discount can reduce either
    // display without changing the variant price posted to cart.
    const price = this.#displayPrice(variant, pricedBoxes, subscribe);
    // Compare-at: the full no-discount price for this size. Prefer the variant's
    // compare_at; if absent, fall back to one-time price (so subscribe still
    // shows its saving vs one-time).
    const compareAt = this.#displayDiscountPercent > 0
      ? basePrice
      : variant.compareAt && variant.compareAt > price
        ? variant.compareAt
        : subscribe
          ? variant.price
          : null;

    priceEl.textContent = this.#formatMoney(price);

    const compareEl = this.querySelector('[ref="comparePrice"]');
    const compareGroup = this.querySelector('[ref="compareGroup"]');
    if (compareEl) {
      const showCompare = compareAt && compareAt > price;
      if (showCompare) compareEl.textContent = this.#formatMoney(compareAt);
      if (compareGroup) compareGroup.hidden = !showCompare;
      compareEl.hidden = !showCompare;
    }

    // Percentage-based, matching the first-paint "(Save X%)" note.
    const pct = compareAt && compareAt > price ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
    const savingsEl = this.querySelector('[ref="savings"]');
    if (savingsEl) {
      if (pct > 0) {
        savingsEl.textContent = `(Save ${pct}%)`;
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }

    // Per-box price. When below the minimum, base it on the indicative
    // variant's own box count rather than the live count.
    // Optional info-row item below the button (only rendered if configured).
    const perBoxCount = pricedBoxes;
    const perBox = this.querySelector('[ref="perBoxInfo"]');
    if (perBox && perBoxCount > 0) {
      perBox.textContent = `${this.#formatMoney(Math.round(price / perBoxCount))}/box`;
    }

    // Single/Bundle toggle: recalc "$X.XX / stick | NNct" for the current size.
    this.#updateToggleTexts(variant, perBoxCount);
  }

  #updateFormProperties() {
    const form = this.querySelector("form") || this.querySelector(".bundle-product-form");
    if (!form) return;

    // Enable + fill the active box properties; disable the rest so empty
    // properties never reach checkout.
    for (let i = 1; i <= this.#max; i++) {
      const input = form.querySelector(`[ref="boxProperty${i}"]`);
      if (!input) continue;
      const box = this.#boxes[i - 1];
      if (box) {
        input.disabled = false;
        input.value = box.title;
      } else {
        input.disabled = true;
        input.value = "";
      }
    }

    // Identical payload shape to assets/bundle-builder.js (`_bundle`), so the
    // backend processes both builders the same way — the only difference is
    // the number of slots (1..max instead of a fixed 3 + bonus).
    const bundleInput = form.querySelector('[ref="bundleProperty"]');
    if (bundleInput) {
      bundleInput.value = JSON.stringify({
        schema_version: 1,
        bundle_type: "sponsored_athlete",
        slots: this.#boxes.map((b, i) => ({
          slot: `box_${i + 1}`,
          variant_id: b.variant_id || null,
          product_id: b.product_id || null,
          product_handle: b.productId || null,
          sku: b.sku || null,
          title: b.title || null,
        })),
      });
    }

    this.#syncSubscriptionUuid();
  }

  #updateAddToCartButton() {
    const count = this.#boxes.length;
    const button = this.querySelector('[ref="addToCartButton"]') || this.querySelector("[data-bundle-add-to-cart-button]");
    const buttonText = this.querySelector(".bundle-builder__button-text");
    const variant = this.#currentVariant();
    const ready = count >= this.#min && !!variant;

    if (button) button.disabled = !ready;
    if (buttonText) {
      if (ready) {
        // Per-box price for the selected mode on the button:
        // "Add to Gym Bag • $39/box"
        const subscribe = this.#isSubscribeSelected();
        const price = this.#displayPrice(variant, count, subscribe);
        buttonText.textContent = `Add to Gym Bag • ${this.#formatMoney(Math.round(price / count))}/box`;
      } else if (count >= this.#min && !variant) {
        // Product JSON can fail independently from flavor JSON (for example
        // after a product-handle rename). Never show an impossible negative
        // "Select N more" count while the bundle variant is unavailable.
        buttonText.textContent = "Bundle temporarily unavailable";
      } else {
        const remaining = this.#min - count;
        buttonText.textContent = `Select ${remaining} more ${remaining === 1 ? "box" : "boxes"}`;
      }
    }

    document.dispatchEvent(
      new CustomEvent("bundle:completion-changed", {
        detail: { complete: ready, filled: count, required: this.#min },
      })
    );
  }
}

if (!customElements.get("bundle-builder-flex-component")) {
  customElements.define("bundle-builder-flex-component", BundleBuilderFlexComponent);
}
